import { reactive, toRef, computed, onBeforeUnmount, ref } from 'vue';
import store from '@/store';
import type { IBreadcrumbLink } from '@/store/modules/BreadcrumbsModule';

export interface PageToolbarOptions {
  title?: string;
  subtitle?: string;
  showBack?: boolean;
  showSearch?: boolean;
  searchPlaceholder?: string;
  showFilter?: boolean;
  addLabel?: string;
  addPermissions?: string[];
  /** Header image banner URL (e.g. media/page-topics/warehouse.png) */
  backgroundUrl?: string;
  onAdd?: (() => void) | null;
  onFilter?: (() => void) | null;
  onBack?: (() => void) | null;
}

interface PageToolbarState extends Required<
  Omit<PageToolbarOptions, 'onAdd' | 'onFilter' | 'onBack'>
> {
  enabled: boolean;
  search: string;
  showActions: boolean;
  showApproval: boolean;
  onAdd: (() => void) | null;
  onFilter: (() => void) | null;
  onBack: (() => void) | null;
}

const DEFAULT_STATE: PageToolbarState = {
  enabled: false,
  title: '',
  subtitle: '',
  showBack: true,
  showSearch: false,
  searchPlaceholder: 'Tìm kiếm...',
  showFilter: false,
  showActions: false,
  showApproval: false,
  addLabel: '',
  addPermissions: [],
  backgroundUrl: '',
  search: '',
  onAdd: null,
  onFilter: null,
  onBack: null,
};

const pageToolbarState = reactive<PageToolbarState>({ ...DEFAULT_STATE });
const filtersOpen = ref(false);
const actionsOpen = ref(false);
const approvalOpen = ref(false);
/** Pages that mount PageHeaderFilters register here so the button stays visible. */
let filterPanelRegistrations = 0;
/** Pages that mount PageHeaderActions register here so the button stays visible. */
let actionsPanelRegistrations = 0;
/** Pages that mount PageHeaderApprovals register here so the button stays visible. */
let approvalPanelRegistrations = 0;

const BANNER_BODY_CLASS = 'header-page-banner';
const TOOLBAR_BODY_CLASS = 'header-page-toolbar';

function syncBodyClasses(showToolbar: boolean): void {
  if (typeof document === 'undefined') {
    return;
  }
  document.body.classList.toggle(TOOLBAR_BODY_CLASS, showToolbar);
  const bannerActive =
    showToolbar &&
    pageToolbarState.enabled &&
    Boolean(pageToolbarState.backgroundUrl);
  document.body.classList.toggle(BANNER_BODY_CLASS, bannerActive);
}

function breadcrumbSubtitle(pageTitle: string): string {
  const links =
    (store.getters.breadcrumbLinksItems as IBreadcrumbLink[] | undefined) ||
    [];
  const parts = links
    .map((link) => link.text?.trim())
    .filter((text): text is string => Boolean(text) && text !== 'Trang chủ');

  if (!parts.length) {
    return '';
  }

  const withoutTitle = parts.filter((text) => text !== pageTitle);
  if (withoutTitle.length) {
    return withoutTitle.join(' · ');
  }

  return parts.join(' · ');
}

function syncFilterVisibility(): void {
  // Button is visible only while a page has PageHeaderFilters mounted
  pageToolbarState.showFilter = filterPanelRegistrations > 0;
  if (!pageToolbarState.showFilter) {
    filtersOpen.value = false;
  }
}

function syncActionsVisibility(): void {
  pageToolbarState.showActions = actionsPanelRegistrations > 0;
  if (!pageToolbarState.showActions) {
    actionsOpen.value = false;
  }
}

function syncApprovalVisibility(): void {
  pageToolbarState.showApproval = approvalPanelRegistrations > 0;
  if (!pageToolbarState.showApproval) {
    approvalOpen.value = false;
  }
}

/**
 * Shared page toolbar config rendered inside `#kt_header` for the whole app.
 */
export function usePageToolbar() {
  const storeTitle = computed(() => (store.getters.pageTitle as string) || '');

  const displayTitle = computed(() => {
    if (pageToolbarState.enabled && pageToolbarState.title) {
      return pageToolbarState.title;
    }
    return storeTitle.value;
  });

  const displaySubtitle = computed(() => {
    if (pageToolbarState.enabled && pageToolbarState.subtitle) {
      return pageToolbarState.subtitle;
    }
    return breadcrumbSubtitle(displayTitle.value);
  });

  const showBack = computed(() => {
    if (pageToolbarState.enabled) {
      return pageToolbarState.showBack;
    }
    return true;
  });

  const hasHeaderBanner = computed(
    () => pageToolbarState.enabled && Boolean(pageToolbarState.backgroundUrl),
  );

  const searchQuery = toRef(pageToolbarState, 'search');

  function setPageToolbar(options: PageToolbarOptions): void {
    pageToolbarState.enabled = true;
    pageToolbarState.title = options.title ?? '';
    pageToolbarState.subtitle = options.subtitle ?? '';
    pageToolbarState.showBack = options.showBack ?? true;
    pageToolbarState.showSearch = options.showSearch ?? false;
    pageToolbarState.searchPlaceholder =
      options.searchPlaceholder ?? 'Tìm kiếm...';
    pageToolbarState.showFilter = options.showFilter ?? false;
    pageToolbarState.addLabel = options.addLabel ?? '';
    pageToolbarState.addPermissions = options.addPermissions ?? [];
    pageToolbarState.backgroundUrl = options.backgroundUrl ?? '';
    pageToolbarState.onAdd = options.onAdd ?? null;
    pageToolbarState.onFilter = options.onFilter ?? null;
    pageToolbarState.onBack = options.onBack ?? null;
    pageToolbarState.search = '';
    filtersOpen.value = false;
    actionsOpen.value = false;
    approvalOpen.value = false;
    syncFilterVisibility();
    syncActionsVisibility();
    syncApprovalVisibility();
  }

  function clearPageToolbar(): void {
    Object.assign(pageToolbarState, {
      ...DEFAULT_STATE,
      addPermissions: [],
      search: '',
      onAdd: null,
      onFilter: null,
      onBack: null,
    });
    filtersOpen.value = false;
    actionsOpen.value = false;
    approvalOpen.value = false;
    syncFilterVisibility();
    syncActionsVisibility();
    syncApprovalVisibility();
  }

  function registerFilterPanel(active: boolean): void {
    filterPanelRegistrations = Math.max(
      0,
      filterPanelRegistrations + (active ? 1 : -1),
    );
    syncFilterVisibility();
  }

  function registerActionsPanel(active: boolean): void {
    actionsPanelRegistrations = Math.max(
      0,
      actionsPanelRegistrations + (active ? 1 : -1),
    );
    syncActionsVisibility();
  }

  function registerApprovalPanel(active: boolean): void {
    approvalPanelRegistrations = Math.max(
      0,
      approvalPanelRegistrations + (active ? 1 : -1),
    );
    syncApprovalVisibility();
  }

  function usePageToolbarLifecycle(options: PageToolbarOptions): void {
    setPageToolbar(options);
    onBeforeUnmount(() => {
      clearPageToolbar();
    });
  }

  return {
    pageToolbar: pageToolbarState,
    displayTitle,
    displaySubtitle,
    showBack,
    hasHeaderBanner,
    searchQuery,
    filtersOpen,
    actionsOpen,
    approvalOpen,
    setPageToolbar,
    clearPageToolbar,
    registerFilterPanel,
    registerActionsPanel,
    registerApprovalPanel,
    usePageToolbarLifecycle,
    syncBodyClasses,
  };
}
